/**
 * utilities/sentry.ts
 * desc: Sentry event filtering — drops error events raised wholly outside our own code
 * note: wired into Sentry.init as beforeSend (see src/index.jsx)
 */
import type { ErrorEvent, StackFrame } from '@sentry/react';

/**
 * _getFrames
 * desc: collect every stack frame an event carries, across exceptions and threads
 * @param  event  a Sentry error event
 * @return        flat list of frames (empty when the event carries no stack)
 */
function _getFrames(event: ErrorEvent): StackFrame[] {
    const values = [ ...(event.exception?.values ?? []), ...(event.threads?.values ?? []) ];
    return values.flatMap(value => value.stacktrace?.frames ?? []);
}

/**
 * _isOwnFrame
 * desc: whether a single frame points at code we serve ourselves
 * note: accepts both frame representations — the browser stack gives absolute urls
 *       (`https://host/build/index-x.js`), while a frame relativized against the
 *       page origin keeps only the path (`/build/index-x.js`). Injected third-party
 *       code matches neither: extensions report a `chrome-extension://` url, and
 *       inline/eval'd injections report no filename at all (`<anonymous>`)
 * @param  frame  a Sentry stack frame
 * @return        true when the frame is ours
 */
function _isOwnFrame(frame: StackFrame): boolean {
    const path = frame.abs_path || frame.filename || '';
    return path.startsWith(window.location.origin) || path.startsWith('/');
}

/**
 * isOwnCodeEvent
 * desc: whether an error event implicates code we actually serve
 * note: our frames are same-origin — hashed bundle chunks under /build/, the worker,
 *       dev-server modules, and the document itself for the inline GTM snippet. An
 *       event with no stack whatsoever is kept: there is nothing to attribute it to,
 *       which covers captureMessage, console logs and cross-origin "Script error."
 * @param  event  a Sentry error event
 * @return        true when the event should be reported
 */
export function isOwnCodeEvent(event: ErrorEvent): boolean {
    const frames = _getFrames(event);
    if (!frames.length) return true;

    return frames.some(_isOwnFrame);
}

/**
 * beforeSend
 * desc: Sentry beforeSend hook — discards errors thrown entirely inside injected
 *       third-party scripts
 * note: browser extensions that monkey-patch builtins are a recurring source of
 *       `RangeError: Maximum call stack size exceeded` here (Y-MEADOWS-25A, and
 *       Y-MEADOWS-254 through -257 from a second extension). None of it is
 *       reproducible or fixable from this repo, and it arrives as high-priority
 *       unhandled errors, so it is dropped client-side rather than triaged.
 * @param  event  a Sentry error event
 * @return        the event to report, or null to drop it
 */
export function beforeSend(event: ErrorEvent): ErrorEvent | null {
    return isOwnCodeEvent(event) ? event : null;
}
